/**
 * EmployeeProfileView — the professional Growth/CV profile body of one Digital Employee
 * (DIGEMP-GROWTH-CV-V2-FRONTEND-086; professional CV cover added by
 * DIGEMP-PRODUCTIZATION-GROWTH-UX-112).
 *
 * One read-only operator profile: a CV cover band (the declared-specialty line next to the
 * evaluated-proficiency line, plus an at-a-glance count strip), a summary band plus five
 * drill-down tabs — audited experience
 * (the existing ExperienceTimeline component over the run window the page loads), verified
 * outcomes with this employee's significant work, declared-vs-measured skills, learning goals and
 * the policy envelope (known limits + approved tool qualifications).
 *
 * Data contract: the page composes the profile from the existing audited read contracts the
 * employee workspace already uses — the growth profile aggregate, the audited experience history,
 * the entitlement projection (workspace capabilities.tools) and the agent detail — assembled here by
 * buildProfessionalProfile() with the same semantics as the composed backend profile contract
 * (DIGEMP-GROWTH-CV-V2-BACKEND-086): verified and unverified work stay separate counts with
 * separate labels, only positive terminal work is highlighted as significant work (deterministic
 * order, bounded list), scores stay null until terminal runs exist, and every absent source
 * degrades to an explicit unknown — never to an invented value. Nothing on this screen grants a
 * permission, tool, connection, customer scope or approval authority, and no raw payload,
 * credential or provider field is rendered.
 *
 * Supersedes the unmerged V1 dashboard generation (DIGEMP-GROWTH-CV-DASHBOARD-FRONTEND-055)
 * rather than stacking a parallel CV surface beside it.
 */
import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ExperienceTimeline, OUTCOME_META } from "./EmployeeWorkspace";

export const PROFILE_HIGHLIGHT_LIMIT = 8;

export const UNVERIFIED_NOTE = "עבודה שהושלמה ללא אימות אינה נספרת כהצלחה מאומתת";

export const VERIFICATION_BASIS_LABELS = {
  technician_resolution_outcome: "הערת פתרון של טכנאי",
  approved_close_executed: "סגירת פנייה מאושרת שבוצעה",
};

export const LEARNING_STATUS_LABELS = {
  open: "פתוח",
  acknowledged: "אושר",
};

// Only these two outcomes are "work performed": everything else (failed / cancelled) is an
// outcome record that is counted but never highlighted (same rule as the backend profile).
const POSITIVE_OUTCOMES = ["verified_success", "unverified"];

const ENTRY_OUTCOME_META = {
  verified_success: OUTCOME_META.verified,
  unverified: OUTCOME_META.unverified,
  failed: OUTCOME_META.failed,
  cancelled: OUTCOME_META.cancelled,
};

const TABS = [
  { key: "experience", label: "ניסיון והוכחות" },
  { key: "outcomes", label: "תוצאות מאומתות" },
  { key: "skills", label: "מיומנויות" },
  { key: "learning", label: "למידה" },
  { key: "limits", label: "מגבלות וכלים" },
];

const text = (value) => (typeof value === "string" && value.trim() ? value.trim() : null);
const list = (value) => (Array.isArray(value) ? value : []);

function fmtDateTime(value) {
  if (typeof value !== "string" || !value.trim()) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString("he-IL", { dateStyle: "short", timeStyle: "short" });
}

function fmtRate(value) {
  return typeof value === "number" && Number.isFinite(value) ? `${Math.round(value * 100)}%` : null;
}

function normalizeCvSummary(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  return {
    headline: text(raw.headline),
    sections: list(raw.sections).map((section) => ({
      title: text(section?.title),
      lines: list(section?.lines).map(text).filter(Boolean),
    })),
    text: text(raw.text),
  };
}

/**
 * Assembles the profile view-model from the audited read payloads. Pure; every section is null
 * when its source payload is absent so the UI can render an explicit unknown per section.
 */
export function buildProfessionalProfile({ agent, growthProfile, experienceHistory, workspace } = {}) {
  const gp = growthProfile && typeof growthProfile === "object" ? growthProfile : null;
  const eh = experienceHistory && typeof experienceHistory === "object" ? experienceHistory : null;
  const entries = list(eh?.entries);

  // The declared specialties are the employee's own configuration. When the growth contract
  // carries none, the agent record's own specialty list is the same declaration (the workspace
  // profile card uses the identical fallback); an unusable source still degrades to the explicit
  // empty/unknown states, never to an invented value.
  const declaredSpecialties = list(gp?.declared?.specialties).map(text).filter(Boolean);
  const specialties = declaredSpecialties.length > 0
    ? declaredSpecialties
    : list(agent?.specialties).map(text).filter(Boolean);
  const role = gp
    ? {
        role_title: text(gp?.identity?.role_title) || text(agent?.role_title),
        specialties,
        expertise_statement: text(gp?.declared?.expertise_statement),
      }
    : null;

  const measured = gp?.evaluated_proficiency && typeof gp.evaluated_proficiency === "object"
    ? gp.evaluated_proficiency
    : null;
  const skills = gp
    ? { declared: specialties, measured, learning_goals: list(gp.learning_goals) }
    : null;

  const experience = eh ? { window: eh.limit ?? null, summary: eh.summary ?? null, entries } : null;

  const summary = eh?.summary ?? null;
  let outcomes = null;
  let significantWork = null;
  if (eh && summary) {
    const bases = {};
    for (const entry of entries) {
      const verification = entry?.verification;
      if (verification?.verified && verification?.basis) {
        bases[verification.basis] = (bases[verification.basis] || 0) + 1;
      }
    }
    outcomes = {
      window: eh.limit ?? null,
      scanned_entries: summary.significant_entries ?? 0,
      verified_successes: summary.verified_successes ?? 0,
      unverified_completions: summary.unverified_completions ?? 0,
      failures: summary.failures ?? 0,
      cancellations: summary.cancellations ?? 0,
      verification_bases: Object.fromEntries(Object.entries(bases).sort(([a], [b]) => a.localeCompare(b))),
      unverified_note: UNVERIFIED_NOTE,
    };

    // Deterministic, most significant first — same three stable passes as the backend profile:
    // verified before unverified, then most recent, then entry id descending.
    const positives = entries.filter((entry) => POSITIVE_OUTCOMES.includes(entry?.outcome));
    const ordered = [...positives]
      .sort((a, b) => String(b?.entry_id || "").localeCompare(String(a?.entry_id || "")))
      .sort((a, b) => String(b?.occurred_at || "").localeCompare(String(a?.occurred_at || "")))
      .sort((a, b) => (a?.outcome === "verified_success" ? 0 : 1) - (b?.outcome === "verified_success" ? 0 : 1));
    significantWork = {
      window: eh.limit ?? null,
      scanned_entries: summary.significant_entries ?? 0,
      positive_entries: positives.length,
      verified_successes: summary.verified_successes ?? 0,
      unverified_completions: summary.unverified_completions ?? 0,
      highlights: ordered.slice(0, PROFILE_HIGHLIGHT_LIMIT),
    };
  }

  const capabilities = workspace?.capabilities;
  const tools = list(capabilities?.tools);
  const qualifications = capabilities && Array.isArray(capabilities.tools)
    ? {
        basis: "tool_entitlement_projection",
        total: tools.length,
        approval_gated: tools.filter((tool) => tool?.approval_required || tool?.strong_approval_required).length,
        strong_approval_gated: tools.filter((tool) => tool?.strong_approval_required).length,
        tools,
      }
    : null;

  return {
    role,
    skills,
    experience,
    outcomes,
    significant_work: significantWork,
    qualifications,
    limits: gp?.known_limits && typeof gp.known_limits === "object" ? gp.known_limits : null,
    cv_summary: normalizeCvSummary(gp?.cv_summary),
  };
}

/**
 * The at-a-glance counts of the CV cover. Every entry is null (rendered as an explicit unknown)
 * when its source payload is absent — a missing source never becomes a fabricated zero.
 * Exported for the focused tests.
 */
export function glanceCounts(model) {
  const outcomes = model?.outcomes ?? null;
  const skills = model?.skills ?? null;
  const qualifications = model?.qualifications ?? null;
  const goals = skills ? list(skills.learning_goals) : null;
  return {
    verifiedWork: outcomes ? outcomes.verified_successes : null,
    documentedWork: outcomes ? outcomes.scanned_entries : null,
    openGoals: goals ? goals.filter((goal) => goal?.status === "open").length : null,
    qualifiedTools: qualifications ? qualifications.total : null,
  };
}

/**
 * The professional cover of the CV: the declared-specialty line (violet, "מוצהר" — a declaration,
 * not a measurement) stays visually apart from the evaluated-proficiency line (blue, "נמדד" —
 * derived from terminal run evidence), over one count strip. Every line degrades to an explicit
 * unknown when its source is absent; the cover adds no retry controls (each drill-down tab owns
 * its own retry) and no writable affordance.
 */
function CvCover({ model }) {
  const skills = model?.skills ?? null;
  const declared = skills ? list(skills.declared) : null;
  const measured = skills?.measured ?? null;
  const counts = glanceCounts(model);

  let measuredLine;
  if (!skills) {
    measuredLine = "מיומנות נמדדת: לא ידוע — נתוני ההערכה טרם סופקו";
  } else if (!measured) {
    measuredLine = "מיומנות נמדדת: לא ידוע — תמונת ההערכה טרם סופקה";
  } else if (!measured.has_outcome_evidence) {
    measuredLine = "מיומנות נמדדת: טרם נמדדה — אין עדיין ראיות מהעבודה שהתבצעה";
  } else {
    const score = typeof measured.overall_score === "number" ? Math.round(measured.overall_score) : null;
    const resolution = fmtRate(measured.resolution_rate);
    measuredLine = `מיומנות נמדדת: ${score != null ? `${score} מתוך 100` : "טרם נמדד"}${resolution ? ` · שיעור פתרון ${resolution}` : ""}`;
  }

  const tiles = [
    { key: "verified", label: "עבודות מאומתות", value: counts.verifiedWork, sub: "אושרו על ידי טכנאי" },
    { key: "documented", label: "רשומות עבודה מתועדות", value: counts.documentedWork, sub: "בחלון הראיות שנטען" },
    { key: "goals", label: "יעדי למידה פתוחים", value: counts.openGoals, sub: "פערי ידע במעקב" },
    { key: "tools", label: "כלים מאושרים לעובד", value: counts.qualifiedTools, sub: "לפי מכסת ההרשאות" },
  ];

  return (
    <section aria-label="כרטיס מקצועי" data-testid="cv-cover" className="bg-white rounded-xl border shadow-sm p-5 space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-base font-semibold text-gray-800">במבט מקצועי</h2>
        <span className="text-[11px] text-gray-400">
          הצהרה מול מדידה — מוצהר אינו ראיה, נמדד מבוסס ריצות שהסתיימו
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div data-testid="cv-declared" className="rounded-lg border border-violet-200 bg-violet-50/40 p-3 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] rounded-full border border-violet-200 bg-violet-100 text-violet-700 px-1.5 py-0.5">מוצהר</span>
            <span className="text-[11px] text-violet-700/80">מהגדרת העובד — אינו מדד ביצועים</span>
          </div>
          <p className="text-sm text-violet-900">
            {declared == null
              ? "התמחות מוצהרת: לא ידוע — הנתונים טרם סופקו"
              : declared.length === 0
                ? "התמחות מוצהרת: טרם הוצהרו תחומי התמחות"
                : `התמחות מוצהרת: ${declared.join(" · ")}`}
          </p>
        </div>

        <div data-testid="cv-measured" className="rounded-lg border border-blue-200 bg-blue-50/40 p-3 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] rounded-full border border-blue-200 bg-blue-100 text-blue-700 px-1.5 py-0.5">נמדד</span>
            <span className="text-[11px] text-blue-800/80">מבוסס ריצות שהסתיימו — ראיות מהמערכת</span>
          </div>
          <p className="text-sm text-blue-900">{measuredLine}</p>
        </div>
      </div>

      <div data-testid="profile-glance" className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {tiles.map((tile) => (
          <div key={tile.key} className="bg-white rounded-lg py-2 px-3 border border-gray-200 text-center">
            <div className="font-semibold text-gray-700">{tile.value ?? "—"}</div>
            <div className="text-[11px] text-gray-500 mt-0.5">{tile.label}</div>
            <div className="text-[10px] text-gray-400">{tile.value == null ? "טרם נטען מהשרת" : tile.sub}</div>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-gray-400">
        — משמעו שהנתון טרם נטען מהשרת, ולא אפס; כל שורה נגזרת מהרשומות המבוקרות של העובד ואינה מעניקה הרשאות
      </p>
    </section>
  );
}

function UnknownBlock({ message, onRetry }) {
  return (
    <div className="space-y-2">
      <p role="alert" className="text-sm text-gray-400 italic">{message}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="text-xs px-2.5 py-1.5 rounded-lg border text-gray-600 hover:bg-gray-50"
        >
          נסה שוב
        </button>
      )}
    </div>
  );
}

function OutcomeBadge({ outcome }) {
  const meta = ENTRY_OUTCOME_META[outcome];
  if (!meta) return null;
  return (
    <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${meta.cls}`}>
      {meta.icon} {meta.label}
    </span>
  );
}

function ProfileSummary({ summary, loading, error, onRetry }) {
  return (
    <section
      aria-label="תקציר הפרופיל המקצועי"
      className="bg-white rounded-xl border shadow-sm p-5 space-y-3"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-base font-semibold text-gray-800">תקציר CV</h2>
        <span className="text-[11px] text-gray-400">
          נבנה מהרשומות המבוקרות של העובד — לקריאה בלבד ואינו מעניק הרשאות
        </span>
      </div>

      {summary ? (
        <>
          {summary.headline && <p className="text-sm font-medium text-gray-800">{summary.headline}</p>}
          {summary.sections.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {summary.sections.map((section, index) => (
                <div key={`${section.title}-${index}`} className="rounded-lg border border-gray-200 bg-gray-50/60 p-3 space-y-1.5">
                  {section.title && <h3 className="text-sm font-semibold text-gray-700">{section.title}</h3>}
                  <ul className="space-y-1 text-sm text-gray-700">
                    {section.lines.map((line, lineIndex) => (
                      <li key={`${line}-${lineIndex}`} className="flex gap-1.5">
                        <span aria-hidden="true" className="text-gray-400">•</span>
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : (
            summary.text && <p className="text-sm text-gray-700 leading-6 whitespace-pre-line">{summary.text}</p>
          )}
        </>
      ) : loading ? (
        <p className="text-sm text-gray-400 italic">טוען את תקציר ה-CV…</p>
      ) : (
        <UnknownBlock
          message={error ? "לא ניתן לטעון את תקציר ה-CV (לא ידוע)" : "לא ידוע — תקציר ה-CV טרם סופק"}
          onRetry={error ? onRetry : undefined}
        />
      )}
    </section>
  );
}

function ExperiencePanel({ runs, runsLoading, runsError }) {
  return (
    <div className="space-y-2">
      <ExperienceTimeline runs={runs} isLoading={runsLoading} isError={runsError} />
      <div className="flex items-center justify-between gap-2 flex-wrap text-[11px] text-gray-400">
        <span>רשומות הריצות המבוקרות של העובד; פירוט האימות לכל רשומת עבודה בלשונית תוצאות מאומתות</span>
        <Link to="/agent-runs" className="text-brand-600 hover:underline">לכל ריצות ה-AI ←</Link>
      </div>
    </div>
  );
}

function StatTile({ label, value }) {
  return (
    <div className="bg-white rounded-lg py-2 px-3 border border-gray-200 text-center">
      <div className="font-semibold text-gray-700">{value}</div>
      <div className="text-[11px] text-gray-400 mt-0.5">{label}</div>
    </div>
  );
}

function Highlight({ entry }) {
  const summary = text(entry?.summary);
  const ticket = entry?.ticket && typeof entry.ticket === "object" ? entry.ticket : null;
  const verification = entry?.verification && typeof entry.verification === "object" ? entry.verification : null;
  const runId = text(entry?.provenance?.run_id);
  const occurredAt = fmtDateTime(entry?.occurred_at);
  const basisLabel = verification?.basis
    ? VERIFICATION_BASIS_LABELS[verification.basis] || verification.basis
    : null;
  const lessons = list(entry?.lessons)
    .map((lesson) => text(lesson?.summary))
    .filter(Boolean)
    .slice(0, 3);

  return (
    <li className="rounded-lg border border-gray-100 bg-gray-50/60 p-3 space-y-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <OutcomeBadge outcome={entry?.outcome} />
        {occurredAt && <span className="text-[11px] text-gray-400 whitespace-nowrap">{occurredAt}</span>}
        {runId && (
          <Link
            to={`/agent-runs/${runId}`}
            className="text-[11px] text-blue-600 hover:underline mr-auto"
            aria-label="פתח את ריצת העבודה שממנה נגזרה הרשומה"
          >
            צפה בריצה
          </Link>
        )}
      </div>

      <p className="text-sm text-gray-800">{summary || "ללא סיכום"}</p>

      {ticket?.id && (
        <Link
          to={`/tickets/${ticket.id}`}
          className="text-xs text-blue-600 hover:underline inline-block"
          aria-label={`פתח את הקריאה${ticket.ticket_number ? ` #${ticket.ticket_number}` : ""} שממנה הגיעה העבודה`}
        >
          {ticket.ticket_number ? `קריאה #${ticket.ticket_number}` : "קריאה מקושרת"}
          {ticket.company_name ? ` · ${ticket.company_name}` : ""}
        </Link>
      )}

      {verification?.verified && (
        <p className="text-[11px] text-teal-700">
          אומת{basisLabel ? ` · ${basisLabel}` : ""}
          {verification.evidence ? `: ${verification.evidence}` : ""}
        </p>
      )}
      {verification?.verified === false && (
        <p className="text-[11px] text-amber-700">טרם אומת — אין אימות טכנאי לרשומה זו</p>
      )}

      {lessons.length > 0 && (
        <div className="text-xs text-gray-600 bg-amber-50/70 border border-amber-100 rounded px-2 py-1">
          <span className="font-medium text-amber-800">מועמדים ללמידה:</span>
          <ul className="list-disc pr-4 mt-0.5 space-y-0.5">
            {lessons.map((lesson, index) => <li key={`${lesson}-${index}`}>{lesson}</li>)}
          </ul>
        </div>
      )}
    </li>
  );
}

function OutcomesPanel({ model, loading, error, onRetry }) {
  if (!model.outcomes || !model.significant_work) {
    if (loading) return <p className="text-sm text-gray-400 italic">טוען תוצאות מבוקרות…</p>;
    if (error) return <UnknownBlock message="לא ניתן לטעון את התוצאות המבוקרות (לא ידוע)" onRetry={onRetry} />;
    return <UnknownBlock message="לא ידוע — נתוני התוצאות טרם סופקו" />;
  }

  const outcomes = model.outcomes;
  const work = model.significant_work;

  if (outcomes.scanned_entries === 0) {
    return (
      <p className="text-sm text-gray-400 italic">אין עדיין תוצאות מתועדות בחלון הנסרק</p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <p className="text-xs text-gray-500">
          מחושב על {outcomes.scanned_entries} הרשומות האחרונות שהסתיימו (חלון {outcomes.window})
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <StatTile label="תוצאות מאומתות" value={outcomes.verified_successes} />
          <StatTile label="הושלמו ללא אימות" value={outcomes.unverified_completions} />
          <StatTile label="כשלונות" value={outcomes.failures} />
          <StatTile label="ביטולים" value={outcomes.cancellations} />
        </div>
        <p className="text-[11px] text-amber-700">{outcomes.unverified_note}</p>
      </div>

      {Object.keys(outcomes.verification_bases).length > 0 && (
        <div className="text-xs text-gray-600">
          <span className="font-medium">ביסוס האימות:</span>{" "}
          {Object.entries(outcomes.verification_bases)
            .map(([basis, count]) => `${VERIFICATION_BASIS_LABELS[basis] || basis}: ${count}`)
            .join(" · ")}
        </div>
      )}

      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h3 className="text-sm font-semibold text-gray-700">עבודה משמעותית</h3>
          <span className="text-[11px] text-gray-400">
            {work.positive_entries} עבודות שהושלמו ({work.verified_successes} מאומתות, {work.unverified_completions} ללא אימות)
          </span>
        </div>
        {work.highlights.length === 0 ? (
          <p className="text-sm text-gray-400 italic">אין עבודות שהושלמו להצגה בחלון זה</p>
        ) : (
          <ol className="space-y-2">
            {work.highlights.map((entry) => <Highlight key={entry.entry_id} entry={entry} />)}
          </ol>
        )}
      </div>
    </div>
  );
}

function SkillsPanel({ model }) {
  const skills = model.skills;
  if (!skills) {
    return <UnknownBlock message="לא ידוע — נתוני המיומנויות טרם סופקו" />;
  }
  const measured = skills.measured;
  const evidence = measured?.evidence && typeof measured.evidence === "object" ? measured.evidence : null;
  const overall = typeof measured?.overall_score === "number" ? Math.round(measured.overall_score) : null;
  const resolution = fmtRate(measured?.resolution_rate);
  const evaluatedAt = fmtDateTime(measured?.evaluated_at);

  return (
    <div className="space-y-4">
      <div data-testid="declared-specialties" className="rounded-lg border border-violet-200 bg-violet-50/40 p-3 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-sm font-semibold text-violet-800">התמחות מוצהרת</h3>
          <span className="text-[10px] rounded-full border border-violet-200 bg-violet-100 text-violet-700 px-1.5 py-0.5">מוצהר</span>
        </div>
        <p className="text-[11px] text-violet-700/80">מהגדרת העובד — אינו מדד ביצועים ואינו מעניק הרשאות</p>
        {skills.declared.length === 0 ? (
          <p className="text-sm text-gray-400 italic">לא הוצהרו תחומי התמחות</p>
        ) : (
          <div className="flex flex-wrap gap-1">
            {skills.declared.map((specialty, index) => (
              <span key={`${specialty}-${index}`} className="text-[11px] px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 border border-violet-200">
                {specialty}
              </span>
            ))}
          </div>
        )}
        {model.role?.expertise_statement && (
          <p className="text-xs text-violet-800/90">{model.role.expertise_statement}</p>
        )}
      </div>

      <div data-testid="evaluated-proficiency" className="rounded-lg border border-blue-200 bg-blue-50/40 p-3 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-sm font-semibold text-blue-800">מיומנות נמדדת</h3>
          <span className="text-[10px] rounded-full border border-blue-200 bg-blue-100 text-blue-700 px-1.5 py-0.5">נמדד</span>
        </div>
        <p className="text-[11px] text-blue-800/80">מבוסס ריצות שהסתיימו — ראיות נמדדות מהמערכת</p>

        {!measured ? (
          <p className="text-sm text-gray-400 italic">לא ידוע — נתוני הערכה טרם סופקו</p>
        ) : (
          <>
            {evidence && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <StatTile label="ריצות מתועדות" value={evidence.total_runs} />
                <StatTile label="הושלמו" value={evidence.completed_runs} />
                <StatTile label="נכשלו" value={evidence.failed_runs} />
                <StatTile label="פתוחות" value={evidence.pending_runs} />
              </div>
            )}
            {measured.has_outcome_evidence ? (
              <div className="space-y-2">
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-700">{overall ?? "—"}</div>
                    <div className="text-[11px] text-gray-400">ציון מוערך כולל</div>
                  </div>
                  <div className="text-sm text-gray-600">
                    שיעור פתרון מתוך הריצות שהסתיימו: {resolution ?? "טרם נמדד"}
                  </div>
                </div>
                {list(measured.by_category).length > 0 && (
                  <ul className="space-y-1.5">
                    {measured.by_category.map((category, index) => {
                      const rate = fmtRate(category?.completion_rate);
                      return (
                        <li key={`${category?.category}-${index}`} className="flex items-center justify-between gap-3 text-sm">
                          <span className="text-gray-700">{text(category?.category) || "קטגוריה ללא שם"}</span>
                          <span className="text-xs text-gray-500">
                            {category?.completed ?? 0} מתוך {category?.total ?? 0}
                            {rate ? ` (${rate})` : ""}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
                {evaluatedAt && <p className="text-[11px] text-gray-400">נמדד לאחרונה: {evaluatedAt}</p>}
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">
                אין עדיין ראיות להערכה — המומחיות המוצהרת אינה מהווה הערכה
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function LearningPanel({ model, agentId = null, loading, error, onRetry }) {
  let body;
  if (!model.skills) {
    if (loading) body = <p className="text-sm text-gray-400 italic">טוען יעדי למידה…</p>;
    else if (error) body = <UnknownBlock message="לא ניתן לטעון את יעדי הלמידה (לא ידוע)" onRetry={onRetry} />;
    else body = <UnknownBlock message="לא ידוע — יעדי למידה טרם סופקו" />;
  } else if (model.skills.learning_goals.length === 0) {
    body = <p className="text-sm text-gray-400 italic">אין פערי ידע פתוחים המשויכים לעובד</p>;
  } else {
    const goals = model.skills.learning_goals;
    body = (
      <ul className="space-y-2">
      {goals.map((goal, index) => {
        const topic = text(goal?.topic);
        const category = text(goal?.category);
        const statusLabel = LEARNING_STATUS_LABELS[goal?.status] || text(goal?.status);
        const lastSeen = fmtDateTime(goal?.last_seen_at);
        return (
          <li key={goal?.goal_id || index} className="rounded-lg border border-gray-100 bg-gray-50/60 p-3 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-gray-800 font-medium">
                {topic || "פער ידע ללא נושא מוגדר"}
              </span>
              {category && (
                <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200">
                  {category}
                </span>
              )}
              {statusLabel && (
                <span className={`text-[11px] px-1.5 py-0.5 rounded-full border ${
                  goal?.status === "open"
                    ? "bg-amber-50 text-amber-700 border-amber-200"
                    : "bg-teal-50 text-teal-700 border-teal-200"
                }`}>
                  {statusLabel}
                </span>
              )}
            </div>
            <p className="text-[11px] text-gray-500">
              נצפה {goal?.occurrence_count ?? 0} פעמים · מבוסס {goal?.source_run_count ?? 0} ריצות
              {lastSeen ? ` · נראה לאחרונה: ${lastSeen}` : ""}
            </p>
          </li>
        );
      })}
      </ul>
    );
  }

  return (
    <div className="space-y-2">
      {body}
      {agentId && (
        <p className="text-[11px]">
          <Link
            to={`/agents/${agentId}/learning-center`}
            className="text-brand-600 hover:underline"
          >
            לציר הלמידה המלא — מרכז הלמידה של העובד ←
          </Link>
        </p>
      )}
    </div>
  );
}

// Policy values arrive as machine identifiers ("tier_1", "approval_required"). The human meaning
// leads the line; the identifier stays visible as muted secondary text so operators can still
// match it against the configuration screens.
function MachineToken({ children }) {
  return <span dir="ltr" className="font-mono text-[11px] text-gray-500">{children}</span>;
}

function ToolChip({ tool }) {
  const name = text(tool?.name);
  if (!name) return null;
  return (
    <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-white text-gray-700 border border-gray-200">
      <span dir="ltr">{name}</span>
      {tool?.strong_approval_required && (
        <span className="text-[10px] px-1 rounded-full bg-red-50 text-red-700 border border-red-200">אישור חזק</span>
      )}
      {tool?.approval_required && !tool?.strong_approval_required && (
        <span className="text-[10px] px-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">אישור נדרש</span>
      )}
    </span>
  );
}

function LimitsPanel({ model, limitsError, qualificationsLoading, qualificationsError, onRetryQualifications, onRetryLimits }) {
  const limits = model.limits;
  const qualifications = model.qualifications;

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-gray-200 bg-gray-50/60 p-3 space-y-2">
        <h3 className="text-sm font-semibold text-gray-700">מגבלות ידועות — מעטפת המדיניות</h3>
        {limits ? (
          <ul className="space-y-1.5 text-sm text-gray-700">
            <li>
              רמת הרשאה:{" "}
              {limits.permission_tier_meaning ? (
                <>
                  <span className="font-medium">{limits.permission_tier_meaning}</span>{" "}
                  <MachineToken>{limits.permission_tier}</MachineToken>
                </>
              ) : (
                <MachineToken>{limits.permission_tier}</MachineToken>
              )}
            </li>
            <li>
              רמת אוטונומיה:{" "}
              {limits.autonomy_level_meaning ? (
                <>
                  <span className="font-medium">{limits.autonomy_level_meaning}</span>{" "}
                  <MachineToken>{limits.autonomy_level}</MachineToken>
                </>
              ) : (
                <MachineToken>{limits.autonomy_level}</MachineToken>
              )}
            </li>
            {!limits.is_enabled && <li className="text-red-600">העובד מושבת — אינו מקבל עבודה חדשה</li>}
            <li>
              {Array.isArray(limits.allowed_tools_restriction)
                ? `מוגבל לרשימת כלים: ${limits.allowed_tools_restriction.join(", ")}`
                : "לא הוגדרה הגבלת רשימת כלים"}
            </li>
            {list(limits.approval_required_tools).length > 0 && (
              <li>
                כלים הדורשים אישור אדם לפני ביצוע: {limits.approval_required_tools.join(", ")}
              </li>
            )}
            {list(limits.strong_approval_tools).length > 0 && (
              <li>כלים הדורשים אישור חזק: {limits.strong_approval_tools.join(", ")}</li>
            )}
          </ul>
        ) : (
          <UnknownBlock
            message={limitsError ? "לא ניתן לטעון את מעטפת המדיניות (לא ידוע)" : "לא ידוע — מעטפת המדיניות טרם סופקה"}
            onRetry={limitsError ? onRetryLimits : undefined}
          />
        )}
      </div>

      <div className="rounded-lg border border-gray-200 bg-gray-50/60 p-3 space-y-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h3 className="text-sm font-semibold text-gray-700">כלים מאושרים</h3>
          {qualifications && (
            <span className="text-[11px] text-gray-400">
              {qualifications.total} כלים מאושרים לפי מדיניות ההרשאות
              {qualifications.approval_gated > 0
                ? ` · ${qualifications.approval_gated} דורשים אישור (${qualifications.strong_approval_gated} אישור חזק)`
                : ""}
            </span>
          )}
        </div>
        {qualifications ? (
          qualifications.total === 0 ? (
            <p className="text-sm text-gray-400 italic">לא הוקצו כלים מאושרים לפי מדיניות ההרשאות הנוכחית</p>
          ) : (
            <div className="flex flex-wrap gap-1">
              {qualifications.tools.map((tool) => <ToolChip key={tool?.name} tool={tool} />)}
            </div>
          )
        ) : qualificationsLoading ? (
          <p className="text-sm text-gray-400 italic">טוען פריסת הרשאות…</p>
        ) : qualificationsError ? (
          <UnknownBlock message="לא ניתן לטעון את פריסת ההרשאות (לא ידוע)" onRetry={onRetryQualifications} />
        ) : (
          <UnknownBlock message="לא ידוע — פריסת ההרשאות טרם סופקה" />
        )}
        <p className="text-[11px] text-gray-400">תצוגה בלבד — הפרופיל אינו מעניק כלים, הרשאות או יכולת ביצוע</p>
      </div>
    </div>
  );
}

export default function EmployeeProfileView({
  agent,
  profile = null,
  profileLoading = false,
  profileError = false,
  onRetryProfile,
  experienceHistory = null,
  experienceLoading = false,
  experienceError = false,
  onRetryExperience,
  workspace = null,
  workspaceLoading = false,
  workspaceError = false,
  onRetryWorkspace,
  runs = [],
  runsLoading = false,
  runsError = false,
}) {
  const [tab, setTab] = useState("experience");
  const tabRefs = useRef({});

  const model = useMemo(
    () => buildProfessionalProfile({ agent, growthProfile: profile, experienceHistory, workspace }),
    [agent, profile, experienceHistory, workspace]
  );

  const onTabKeyDown = (event, index) => {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    let next = index;
    if (event.key === "ArrowLeft") next = (index + 1) % TABS.length;
    if (event.key === "ArrowRight") next = (index - 1 + TABS.length) % TABS.length;
    if (event.key === "Home") next = 0;
    if (event.key === "End") next = TABS.length - 1;
    setTab(TABS[next].key);
    tabRefs.current[TABS[next].key]?.focus();
  };

  return (
    <div className="space-y-5">
      <CvCover model={model} />

      <ProfileSummary
        summary={model.cv_summary}
        loading={profileLoading}
        error={profileError}
        onRetry={onRetryProfile}
      />

      <div className="space-y-3">
        <div role="tablist" aria-label="ניווט בפרופיל המקצועי" className="flex flex-wrap gap-1 border-b">
          {TABS.map((entry, index) => {
            const selected = tab === entry.key;
            return (
              <button
                key={entry.key}
                ref={(node) => { tabRefs.current[entry.key] = node; }}
                type="button"
                role="tab"
                id={`profile-tab-${entry.key}`}
                aria-selected={selected}
                aria-controls={`profile-tabpanel-${entry.key}`}
                tabIndex={selected ? 0 : -1}
                onClick={() => setTab(entry.key)}
                onKeyDown={(event) => onTabKeyDown(event, index)}
                className={`text-sm px-3 py-2 -mb-px border-b-2 transition-colors ${
                  selected
                    ? "border-brand-600 text-brand-700 font-medium"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                {entry.label}
              </button>
            );
          })}
        </div>

        {tab === "experience" && (
          <div role="tabpanel" id="profile-tabpanel-experience" aria-labelledby="profile-tab-experience" tabIndex={0}>
            <ExperiencePanel runs={runs} runsLoading={runsLoading} runsError={runsError} />
          </div>
        )}

        {tab === "outcomes" && (
          <div role="tabpanel" id="profile-tabpanel-outcomes" aria-labelledby="profile-tab-outcomes" tabIndex={0}>
            <OutcomesPanel
              model={model}
              loading={experienceLoading}
              error={experienceError}
              onRetry={onRetryExperience}
            />
          </div>
        )}

        {tab === "skills" && (
          <div role="tabpanel" id="profile-tabpanel-skills" aria-labelledby="profile-tab-skills" tabIndex={0}>
            <SkillsPanel model={model} />
          </div>
        )}

        {tab === "learning" && (
          <div role="tabpanel" id="profile-tabpanel-learning" aria-labelledby="profile-tab-learning" tabIndex={0}>
            <LearningPanel
              model={model}
              agentId={agent?.id ?? null}
              loading={profileLoading}
              error={profileError}
              onRetry={onRetryProfile}
            />
          </div>
        )}

        {tab === "limits" && (
          <div role="tabpanel" id="profile-tabpanel-limits" aria-labelledby="profile-tab-limits" tabIndex={0}>
            <LimitsPanel
              model={model}
              limitsError={profileError}
              qualificationsLoading={workspaceLoading}
              qualificationsError={workspaceError}
              onRetryQualifications={onRetryWorkspace}
              onRetryLimits={onRetryProfile}
            />
          </div>
        )}
      </div>
    </div>
  );
}
